import {
  authenticatedResponseSchema,
  enrollmentResultSchema,
  passkeyOptionsResponseSchema,
  type AuthenticatedResponse,
  type EnrollmentResult,
} from '@growup/accounts-shared';
import {
  browserSupportsWebAuthn,
  startAuthentication,
  startRegistration,
  WebAuthnError,
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';

import { post, send } from './api';

/**
 * Passkeys (WebAuthn). The options and answers go between identity-api and the
 * browser untouched; identity-api checks them with @simplewebauthn/server.
 */

export const passkeysSupported = (): boolean => browserSupportsWebAuthn();

/** The person closed the browser prompt: not an error worth showing. */
export function wasCancelled(error: unknown): boolean {
  return (
    (error instanceof WebAuthnError && error.code === 'ERROR_CEREMONY_ABORTED') ||
    (error instanceof Error && error.name === 'NotAllowedError')
  );
}

/** Sign-in with a passkey alone (no password). Counts as a second factor. */
export async function signInWithPasskey(): Promise<AuthenticatedResponse> {
  const { ceremonyId, options } = await post(
    '/api/identity/sessions/passkey/options',
    {},
    passkeyOptionsResponseSchema,
  );
  const response = await startAuthentication({
    optionsJSON: options as unknown as PublicKeyCredentialRequestOptionsJSON,
  });
  return post(
    '/api/identity/sessions/passkey/verify',
    { ceremonyId, response },
    authenticatedResponseSchema,
  );
}

/** Passkey as the second step after the password. */
export async function confirmWithPasskey(challengeToken: string): Promise<AuthenticatedResponse> {
  const { ceremonyId, options } = await post(
    '/api/identity/sessions/mfa/passkey/options',
    { challengeToken },
    passkeyOptionsResponseSchema,
  );
  const response = await startAuthentication({
    optionsJSON: options as unknown as PublicKeyCredentialRequestOptionsJSON,
  });
  return post(
    '/api/identity/sessions/mfa/passkey/verify',
    { challengeToken, ceremonyId, response },
    authenticatedResponseSchema,
  );
}

/** Adds a passkey to the signed-in account. */
export async function addPasskey(name: string): Promise<EnrollmentResult> {
  const { ceremonyId, options } = await send(
    'POST',
    '/api/identity/mfa/passkeys/options',
    {},
    passkeyOptionsResponseSchema,
  );
  const response = await startRegistration({
    optionsJSON: options as unknown as PublicKeyCredentialCreationOptionsJSON,
  });
  return send(
    'POST',
    '/api/identity/mfa/passkeys/verify',
    { ceremonyId, response, ...(name.trim() ? { name: name.trim() } : {}) },
    enrollmentResultSchema,
  );
}
